import { useEffect, useState } from "react";
import { fetchTenantTags } from "../../email/services/graph";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../telemetry";

/**
 * Session cache for the tenant tag vocabulary. The tag set is tenant-wide and
 * effectively static for the life of an add-in session, and harvesting it costs
 * one search plus several `$batch` round-trips — so we run that ONCE and reuse
 * it every time the Knowledge Base is re-opened, instead of re-storming Graph on
 * each mount. `inFlight` also collapses concurrent mounts onto a single fetch.
 */
let cachedTags: string[] | null = null;
let inFlight: Promise<string[]> | null = null;

/** Reset the module cache. Test-only. */
export function _resetKnowledgeTagsCache(): void {
  cachedTags = null;
  inFlight = null;
}

/**
 * Load the tenant-wide tag vocabulary once so the tag-filter chips can render on
 * every Knowledge Base page — including the site-collection list, whose items
 * carry no tags of their own. Served from a session cache after the first load
 * (see above). Idle (empty) with no token. Best-effort: a failure logs and
 * yields an empty list (per-page item tags still drive the chips where present).
 */
export function useKnowledgeTags(token: string | null): string[] {
  const { refreshGraphToken } = useAuthState();
  const [tags, setTags] = useState<string[]>(cachedTags ?? []);

  useEffect(() => {
    if (!token) return;
    if (cachedTags) {
      setTags(cachedTags);
      return;
    }

    let cancelled = false;
    // Share one fetch across concurrent mounts; cache the result for later opens.
    inFlight ??= fetchTenantTags(token, refreshGraphToken)
      .then((result) => {
        cachedTags = result;
        logConnectorSuccess("sharepoint", "load-tags");
        return result;
      })
      .catch((e: unknown) => {
        logConnectorFailure("sharepoint", e, "load-tags");
        return [];
      })
      .finally(() => {
        inFlight = null;
      });

    void inFlight.then((result) => {
      if (!cancelled) setTags(result);
    });

    return () => {
      cancelled = true;
    };
  }, [token, refreshGraphToken]);

  return tags;
}
